import { Link } from 'react-router-dom';
import { Card } from '@heroui/react';

import { VisibilityChip, ArchivedChip, TopicChip } from '../../common/StatusChip';
import { firstWords } from '../../../services/text';

// Κάρτα συλλογής στην επισκόπηση (ΛΑ-06).
//
// Ολόκληρη η κάρτα είναι ο σύνδεσμος και όχι μόνο ο τίτλος. Στο πρότυπο του 2019
// ο στόχος ήταν το κείμενο του τίτλου, δηλαδή μια περιοχή μερικών δεκάδων πίξελ
// μέσα σε κάρτα διακοσίων: το πάτημα στο κενό της κάρτας δεν έκανε τίποτα.

/**
 * ⚠ **ΟΛΕΣ ΟΙ ΚΑΡΤΕΣ ΕΧΟΥΝ ΤΟ ΙΔΙΟ ΥΨΟΣ, ΚΑΙ ΤΟ ΠΕΤΥΧΑΙΝΟΥΝ ΜΕ ΔΕΣΜΕΥΣΗ ΧΩΡΟΥ.**
 *
 * Το πλέγμα τεντώνει ήδη τις κάρτες μιας **γραμμής** στο ύψος της ψηλότερης —
 * αλλά αυτό λύνει τη μισή περίπτωση: οι κάρτες ευθυγραμμίζονταν μεταξύ τους και
 * το **περιεχόμενό** τους όχι, οπότε ο τίτλος της μιας καθόταν δίπλα στην
 * περιγραφή της άλλης και το μάτι δεν μπορούσε να σαρώσει κάθετα.
 *
 * Η λύση δεν είναι σταθερό ύψος στην κάρτα —που θα έκοβε περιεχόμενο σε άγνωστο
 * σημείο— αλλά **σταθερό ύψος ανά ζώνη**: οι ετικέτες, ο τίτλος και η περιγραφή
 * καταλαμβάνουν τον ίδιο χώρο είτε τον γεμίζουν είτε όχι. Οι αριθμοί
 * ακουμπάνε στο κάτω μέρος με `mt-auto`, όπως και πριν.
 *
 * **Τίμημα, δηλωμένο:** ο τίτλος ξανακόβεται στη δεύτερη γραμμή, κάτι που είχε
 * αφαιρεθεί επίτηδες επειδή δύο συλλογές με κοινή αρχή τίτλου φαίνονταν
 * ταυτόσημες. Το τίμημα πληρώνεται συνειδητά και **μετριάζεται**: ο πλήρης
 * τίτλος υπάρχει στο `title` του στοιχείου, δηλαδή στον δείκτη και στον
 * αναγνώστη οθόνης.
 *
 * ⚠ **Ο ΤΙΤΛΟΣ ΔΕΝ ΔΕΣΜΕΥΕΙ ΠΛΕΟΝ ΔΥΟ ΓΡΑΜΜΕΣ.** Η πρώτη μορφή του κρατούσε
 * ύψος δύο γραμμών ακόμη και για μονόγραμμο τίτλο, ώστε να ευθυγραμμίζονται οι
 * **ζώνες** και όχι μόνο τα περιγράμματα. Στην οθόνη το κέρδος δεν φάνηκε και το
 * κόστος φάνηκε: κάρτες με σύντομο τίτλο έδειχναν **κενή γραμμή** κάτω από
 * αυτόν, που διαβάζεται ως λείπουσα πληροφορία και όχι ως στοίχιση.
 *
 * **Το ίσο ύψος της κάρτας δεν χάνεται**: το πλέγμα τεντώνει ήδη τη γραμμή του
 * στο ύψος της ψηλότερης, η περιγραφή κρατά τη δική της δεσμευμένη ζώνη, και οι
 * αριθμοί ακουμπάνε στο κάτω μέρος με `mt-auto`. Ό,τι μετακινείται είναι μόνο η
 * **μεσαία** ζώνη, κατά μία γραμμή.
 */
const TITLE_LINES = 'line-clamp-2';

/** Πόσες λέξεις της περιγραφής δείχνει η κάρτα. */
export const DESCRIPTION_WORDS = 150;

export default function CollectionThumb({
    id,
    title,
    topic,
    description,
    visibility,
    archived,
    memberCount,
    resourceCount
}) {
    const summary = firstWords(description, DESCRIPTION_WORDS);

    return (
        <Card className="card--interactive gap-3 p-0">
            <Link
                to={`/collections/${id}`}
                className="flex flex-1 flex-col gap-3 rounded-3xl p-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            >
                {/* ⚠ **ΟΙ ΕΤΙΚΕΤΕΣ ΠΑΝΩ ΑΠΟ ΤΟΝ ΤΙΤΛΟ.** Ήταν από κάτω, δηλαδή ο
                    αναγνώστης μάθαινε **πρώτα** το όνομα και **μετά** αν πρόκειται
                    για δημόσια ή ιδιωτική συλλογή — και η ορατότητα αλλάζει το
                    πλαίσιο μέσα στο οποίο διαβάζεται το όνομα, όπως ο τύπος του
                    πόρου αλλάζει τον τρόπο που διαβάζεται ο τίτλος του.

                    Η **θεματική ενότητα** μπήκε δίπλα τους: είναι το δεύτερο
                    πράγμα που ξεχωρίζει δύο συλλογές με παρόμοιο όνομα, και δεν
                    φαινόταν πουθενά στην επισκόπηση. Αποδίδεται μόνο όταν υπάρχει
                    — το πεδίο είναι προαιρετικό, και κενό chip δεν λέει τίποτα.

                    Η ζώνη έχει **ορισμένο ύψος**: κάρτα χωρίς θέμα ή χωρίς
                    αρχειοθέτηση δεν ανεβάζει τον τίτλο της πιο ψηλά από τις
                    γειτονικές. */}
                <div className="flex min-h-8 flex-wrap items-start gap-2">
                    <VisibilityChip visibility={visibility} />
                    {topic && <TopicChip>{topic}</TopicChip>}
                    {archived && <ArchivedChip />}
                </div>

                <Card.Title title={title} className={`${TITLE_LINES} break-words hyphens-auto first-letter:uppercase`}>
                    {title}
                </Card.Title>

                {/* Η περιγραφή, **πάντοτε παρούσα ως χώρος**. Συλλογή χωρίς
                    περιγραφή αφήνει τη ζώνη κενή αντί να συρρικνώσει την κάρτα:
                    το ύψος είναι ιδιότητα της **επισκόπησης**, όχι του
                    περιεχομένου της κάθε συλλογής.

                    Το `line-clamp-4` και το όριο των λέξεων λύνουν **διαφορετικά**
                    προβλήματα και χρειάζονται και τα δύο: το πρώτο κρατά το ύψος,
                    το δεύτερο κρατά το **κείμενο που φτάνει στον περιηγητή**
                    πεπερασμένο — μια περιγραφή χιλίων λέξεων θα ταξίδευε ολόκληρη
                    για να μείνει κρυμμένη. */}
                <p className="line-clamp-4 min-h-[5rem] text-sm whitespace-pre-line text-muted">
                    {summary}
                </p>

                <div className="mt-auto flex items-center gap-4 border-t border-border pt-3 text-sm text-muted">
                    <span className="flex items-center gap-1.5">
                        <i className="fa-solid fa-users" aria-hidden="true" />
                        {memberCount}
                        <span className="sr-only">μέλη</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                        <i className="fa-solid fa-bookmark" aria-hidden="true" />
                        {resourceCount}
                        <span className="sr-only">πόροι</span>
                    </span>
                </div>
            </Link>
        </Card>
    );
}
